import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShieldCheck, Users, X } from "lucide-react";

const OPTIONS = [
  {
    value: true,
    title: "Yes, I am the policyholder",
    description: "Continue with the standard claim process.",
    icon: ShieldCheck,
  },
  {
    value: false,
    title: "No, I am a relative or representative",
    description: "Submit this claim on behalf of the policyholder.",
    icon: Users,
  },
];

const PolicyholderCheckModal = ({ open, onClose, onConfirm, policyName }) => {
  const [isPolicyholder, setIsPolicyholder] = useState(null);

  const handleClose = () => {
    setIsPolicyholder(null);
    onClose();
  };

  const handleConfirm = () => {
    if (isPolicyholder === null) return;
    onConfirm(isPolicyholder);
    setIsPolicyholder(null);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="flex fixed inset-0 z-50 justify-center items-center p-4"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 backdrop-blur-sm bg-black/40"
            onClick={handleClose}
          />

          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden relative z-10 w-full max-w-lg bg-white rounded-2xl ring-1 ring-gray-100 shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="policyholder-check-title"
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500" />
            <div className="p-6">
              <div className="flex gap-3 items-start">
                <div className="grid place-items-center w-11 h-11 text-white bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg">
                  <ShieldCheck size={22} />
                </div>
                <div className="flex-1">
                  <h3
                    id="policyholder-check-title"
                    className="text-base font-semibold text-gray-900"
                  >
                    Are you the policyholder?
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {policyName
                      ? `Tell us who is submitting this claim for ${policyName}.`
                      : "Tell us who is submitting this claim so we can collect the right details."}
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="grid place-items-center w-9 h-9 text-gray-500 rounded-lg hover:bg-gray-50 hover:text-gray-700"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="grid gap-3 mt-5">
                {OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const selected = isPolicyholder === option.value;
                  return (
                    <button
                      key={String(option.value)}
                      type="button"
                      onClick={() => setIsPolicyholder(option.value)}
                      className={`flex gap-3 items-start p-4 w-full text-left rounded-xl border transition-colors ${
                        selected
                          ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200"
                          : "border-gray-200 bg-white hover:border-emerald-300"
                      }`}
                    >
                      <span
                        className={`grid place-items-center w-10 h-10 rounded-lg ${
                          selected
                            ? "text-white bg-emerald-600"
                            : "text-emerald-700 bg-emerald-50"
                        }`}
                      >
                        <Icon size={18} />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-gray-900">
                          {option.title}
                        </span>
                        <span className="block mt-0.5 text-xs text-gray-600">
                          {option.description}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-3 justify-end mt-5">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 rounded-xl ring-1 ring-gray-200 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={isPolicyholder === null}
                  className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-green-700 rounded-xl shadow-md hover:from-emerald-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PolicyholderCheckModal;
